import axios from "axios";
import React, {
  useEffect,
  useReducer,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import "./AssignedPDF.css";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const PAGE_SIZE = 15;
const DEBOUNCE_MS = 250;

/* ─────────────────────────────────────────────
   REDUCER
───────────────────────────────────────────── */
const initialState = {
  data: [],
  loading: false,
  error: null,
  filter: "all",
  search: "",
  sortKey: null,
  sortDir: "asc",
  page: 1,
  view: "table",
  selectedPdfs: [],
  selectionHistory: [],
  showModal: false,
  showConfirm: false,
  workerName: "",
  phoneNumber: "",
  errors: {},
  assigning: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_DATA":
      return { ...state, data: action.payload, error: null, page: 1 };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_FILTER":
      return { ...state, filter: action.payload, page: 1 };
    case "SET_SEARCH":
      return { ...state, search: action.payload, page: 1 };
    case "SET_SORT":
      return {
        ...state,
        sortKey: action.payload,
        sortDir:
          state.sortKey === action.payload && state.sortDir === "asc"
            ? "desc"
            : "asc",
        page: 1,
      };
    case "SET_PAGE":
      return { ...state, page: action.payload };
    case "SET_VIEW":
      return { ...state, view: action.payload };
    case "TOGGLE_PDF": {
      const prev = state.selectedPdfs;
      const next = prev.includes(action.payload)
        ? prev.filter((p) => p !== action.payload)
        : [...prev, action.payload];
      return {
        ...state,
        selectedPdfs: next,
        selectionHistory: [...state.selectionHistory, prev],
      };
    }
    case "SET_SELECTION": {
      const prev = state.selectedPdfs;
      return {
        ...state,
        selectedPdfs: action.payload,
        selectionHistory: [...state.selectionHistory, prev],
      };
    }
    case "UNDO_SELECTION": {
      if (!state.selectionHistory.length) return state;
      const hist = [...state.selectionHistory];
      const prev = hist.pop();
      return { ...state, selectedPdfs: prev, selectionHistory: hist };
    }
    case "SHOW_MODAL":
      return { ...state, showModal: true };
    case "HIDE_MODAL":
      return {
        ...state,
        showModal: false,
        workerName: "",
        phoneNumber: "",
        errors: {},
      };
    case "SHOW_CONFIRM":
      return { ...state, showConfirm: true };
    case "HIDE_CONFIRM":
      return { ...state, showConfirm: false };
    case "SET_WORKER":
      return {
        ...state,
        workerName: action.payload,
        errors: { ...state.errors, workerName: null },
      };
    case "SET_PHONE":
      return {
        ...state,
        phoneNumber: action.payload,
        errors: { ...state.errors, phoneNumber: null },
      };
    case "SET_ERRORS":
      return { ...state, errors: action.payload };
    case "SET_ASSIGNING":
      return { ...state, assigning: action.payload };
    case "RESET_AFTER_ASSIGN":
      return {
        ...state,
        showModal: false,
        showConfirm: false,
        selectedPdfs: [],
        selectionHistory: [],
        workerName: "",
        phoneNumber: "",
        errors: {},
        assigning: false,
      };
    default:
      return state;
  }
}

/* ─────────────────────────────────────────────
   VALIDATION
───────────────────────────────────────────── */
const PHONE_RE = /^(\+91[\-\s]?)?[6-9]\d{9}$|^\+?[1-9]\d{7,14}$/;
// const NAME_RE = /^[a-zA-Z\s'-]{15,}$/;

function validate(workerName, phoneNumber) {
  const errors = {};
  if (!workerName.trim()) errors.workerName = "Worker name is required";
  // else if (!NAME_RE.test(workerName.trim()))
  //   errors.workerName = "Min 3 chars, letters only";
  if (!phoneNumber.trim()) errors.phoneNumber = "Phone number is required";
  else if (!PHONE_RE.test(phoneNumber.replace(/\s/g, "")))
    errors.phoneNumber = "Invalid phone number";
  return errors;
}

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debouncedValue;
}

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3000
    );
  }, []);
  return { toasts, toast: add };
}

/* ─────────────────────────────────────────────
   ICONS (inline SVG)
───────────────────────────────────────────── */
const Icon = {
  search: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  table: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  ),
  grid: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  close: () => (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  sort: (dir) => {
    if (dir === "asc") {
      return () => (
        <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 3l9 18H3z" />
        </svg>
      );
    }
    return () => (
      <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 21 3 3h18z" />
      </svg>
    );
  },
  sortNone: () => (
    <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
      <path
        d="M12 3l9 18H3zM12 21 3 3h18z"
        fillRule="evenodd"
        clipRule="evenodd"
        opacity="0.3"
      />
    </svg>
  ),
  pdf: () => "📄",
  undo: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </svg>
  ),
  export: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  refresh: () => (
    <svg
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  ),
};

/* ─────────────────────────────────────────────
   EXPORT CSV
───────────────────────────────────────────── */
function exportCSV(data) {
  const headers = ["File Name", "Worker Name", "Phone", "Status"];
  const rows = data.map((r) => [
    r.file_name,
    r.assigned_worker_name || "",
    r.assigned_worker_phone_no || "",
    r.status,
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${c}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "pdf-assignments.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export const AssignedPDF = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { toasts, toast } = useToasts();
  const debouncedSearch = useDebounce(state.search, DEBOUNCE_MS);
  const modalRef = useRef(null);
  const searchRef = useRef(null);

  const navigate = useNavigate();
  useEffect(()=>{
    const login = sessionStorage.getItem("token");
    if(!login || undefined){
      alert("Login First");
      navigate("/adminP");
    }
  });

  /* ── Fetch data ── */
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) dispatch({ type: "SET_LOADING", payload: true });
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/assignedpdfs`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          }
      );
      dispatch({ type: "SET_DATA", payload: res.data.data });
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message });
      toast("Failed to load PDFs. Retrying…", "error");
      setTimeout(() => fetchData(true), 3000);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        dispatch({ type: "HIDE_MODAL" });
        dispatch({ type: "HIDE_CONFIRM" });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "a" && !state.showModal) {
        e.preventDefault();
        const pendingNames = pendingPdfs.map((p) => p.file_name);
        dispatch({ type: "SET_SELECTION", payload: pendingNames });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !state.showModal) {
        e.preventDefault();
        dispatch({ type: "UNDO_SELECTION" });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.showModal, state.data]);

  /* ── Focus trap for modal ── */
  useEffect(() => {
    if (state.showModal && modalRef.current) {
      const firstInput = modalRef.current.querySelector("input");
      firstInput?.focus();
    }
  }, [state.showModal]);

  /* ── Derived data ── */
  const pendingPdfs = useMemo(
    () => state.data.filter((p) => p.status !== "assigned"),
    [state.data]
  );

  const filteredData = useMemo(() => {
    let d = state.data;
    if (state.filter === "assigned")
      d = d.filter((p) => p.status === "assigned");
    if (state.filter === "pending")
      d = d.filter((p) => p.status !== "assigned");
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      d = d.filter(
        (p) =>
          p.file_name?.toLowerCase().includes(q) ||
          p.assigned_worker_name?.toLowerCase().includes(q)
      );
    }
    if (state.sortKey) {
      d = [...d].sort((a, b) => {
        const va = (a[state.sortKey] || "").toString().toLowerCase();
        const vb = (b[state.sortKey] || "").toString().toLowerCase();
        return state.sortDir === "asc"
          ? va.localeCompare(vb)
          : vb.localeCompare(va);
      });
    }
    return d;
  }, [state.data, state.filter, debouncedSearch, state.sortKey, state.sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (state.page - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, state.page]);

  const stats = useMemo(
    () => ({
      total: state.data.length,
      assigned: state.data.filter((p) => p.status === "assigned").length,
      pending: state.data.filter((p) => p.status !== "assigned").length,
      selected: state.selectedPdfs.length,
    }),
    [state.data, state.selectedPdfs]
  );

  /* ── Select all visible pending ── */
  const pendingOnPage = paginated.filter((p) => p.status !== "assigned");
  const allPageSelected =
    pendingOnPage.length > 0 &&
    pendingOnPage.every((p) => state.selectedPdfs.includes(p.file_name));
  const somePageSelected = pendingOnPage.some((p) =>
    state.selectedPdfs.includes(p.file_name)
  );

  const handleSelectAll = () => {
    if (allPageSelected) {
      const pageNames = pendingOnPage.map((p) => p.file_name);
      dispatch({
        type: "SET_SELECTION",
        payload: state.selectedPdfs.filter((s) => !pageNames.includes(s)),
      });
    } else {
      const pageNames = pendingOnPage.map((p) => p.file_name);
      const merged = Array.from(new Set([...state.selectedPdfs, ...pageNames]));
      dispatch({ type: "SET_SELECTION", payload: merged });
    }
  };

  /* ── Column sort ── */
  const handleSort = useCallback((key) => {
    dispatch({ type: "SET_SORT", payload: key });
  }, []);

  /* ── Validate & open confirm ── */
  const handleOpenModal = () => {
    if (state.selectedPdfs.length === 0) {
      toast("Select at least one PDF first", "warn");
      return;
    }
    dispatch({ type: "SHOW_MODAL" });
  };

  const handlePreAssign = () => {
    const errs = validate(state.workerName, state.phoneNumber);
    if (Object.keys(errs).length) {
      dispatch({ type: "SET_ERRORS", payload: errs });
      return;
    }
    dispatch({ type: "SHOW_CONFIRM" });
  };

  /* ── Assign ── */
  const handleAssign = async () => {
    dispatch({ type: "SET_ASSIGNING", payload: true });
    dispatch({ type: "HIDE_CONFIRM" });
    try {
      await axios.post(`${process.env.REACT_APP_ADMIN_API_URL}/assign-worker`, {
        workerName: state.workerName,
        phoneNumber: state.phoneNumber,
        assignedPdfs: state.selectedPdfs.join(","),
      },{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      toast(
        `Assigned ${state.selectedPdfs.length} PDF(s) to ${state.workerName}`,
        "success"
      );
      dispatch({ type: "RESET_AFTER_ASSIGN" });
      fetchData(true);
    } catch (err) {
      toast(err.response?.data?.message || "Assignment failed", "error");
      dispatch({ type: "SET_ASSIGNING", payload: false });
    }
  };

  /* ── Sort header helper ── */
  const SortTh = ({ label, sortKey, className }) => {
    const active = state.sortKey === sortKey;
    return (
      <th
        className={`sortable ${active ? `sort-${state.sortDir}` : ""} ${
          className || ""
        }`}
        onClick={() => handleSort(sortKey)}
        aria-sort={
          active
            ? state.sortDir === "asc"
              ? "ascending"
              : "descending"
            : "none"
        }
      >
        {label}
        <span className="sort-icon">
          {active ? Icon.sort(state.sortDir)() : Icon.sortNone()}
        </span>
      </th>
    );
  };

  /* ── Skeleton rows ── */
  const SkeletonRows = () =>
    Array.from({ length: 8 }).map((_, i) => (
      <tr key={i} className="apdf-skeleton-row">
        {[40, 30, 160, 120, 110, 70].map((w, j) => (
          <td key={j}>
            <div className="apdf-skeleton-cell" style={{ width: w }} />
          </td>
        ))}
      </tr>
    ));

  /* ── Page numbers ── */
  const pageNumbers = () => {
    const pages = [];
    const range = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= state.page - range && i <= state.page + range)
      ) {
        pages.push(i);
      }
    }
    const result = [];
    let prev = null;
    for (const p of pages) {
      if (prev !== null && p - prev > 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="apdf" role="main">
      {/* TOASTS */}
      <button 
        className="back-btn"
        onClick={() => navigate(-1)}
      >
        ⬅ Back
      </button>
      <div className="apdf-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`apdf-toast ${t.type}`} role="alert">
            {t.type === "success" ? "✓" : t.type === "error" ? "✕" : "⚠"}{" "}
            {t.message}
          </div>
        ))}
      </div>

      {/* STATS */}
      <div className="apdf-stats" aria-label="Summary statistics">
        {[
          { label: "Total PDFs", value: stats.total, cls: "" },
          { label: "Assigned", value: stats.assigned, cls: "green" },
          { label: "Pending", value: stats.pending, cls: "amber" },
          { label: "Selected", value: stats.selected, cls: "accent" },
        ].map(({ label, value, cls }) => (
          <div key={label} className="apdf-stat">
            <span className="apdf-stat-label">{label}</span>
            <span className={`apdf-stat-value ${cls}`}>{value}</span>
          </div>
        ))}
      </div>

      {/* HEADER */}
      <div className="apdf-header">
        <h1 className="apdf-title">PDF Assignment Panel</h1>
        <div className="apdf-header-actions">
          <button
            className="apdf-btn apdf-btn-ghost"
            onClick={() =>
              state.selectionHistory.length &&
              dispatch({ type: "UNDO_SELECTION" })
            }
            disabled={!state.selectionHistory.length}
            title="Undo selection (Ctrl+Z)"
            aria-label="Undo last selection"
          >
            {Icon.undo()} Undo
          </button>
          <button
            className="apdf-btn apdf-btn-ghost"
            onClick={() => exportCSV(filteredData)}
            title="Export to CSV"
          >
            {Icon.export()} Export
          </button>
          <button
            className="apdf-btn apdf-btn-ghost"
            onClick={() => fetchData()}
            title="Refresh data"
            aria-label="Refresh"
          >
            {Icon.refresh()}
          </button>
          <button
            className="apdf-btn apdf-btn-primary"
            onClick={handleOpenModal}
            disabled={stats.selected === 0 || state.assigning}
            aria-label={`Assign ${stats.selected} selected PDFs`}
          >
            {state.assigning
              ? "Assigning…"
              : `Assign Selected (${stats.selected})`}
          </button>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="apdf-controls">
        <div className="apdf-search-wrap">
          <Icon.search />
          <input
            ref={searchRef}
            className="apdf-search"
            type="search"
            placeholder="Search by filename or worker… (Ctrl+F)"
            value={state.search}
            onChange={(e) =>
              dispatch({ type: "SET_SEARCH", payload: e.target.value })
            }
            aria-label="Search PDFs"
          />
        </div>

        <div className="apdf-filters" role="group" aria-label="Filter PDFs">
          {[
            { key: "all", label: `All (${stats.total})` },
            { key: "pending", label: `Pending (${stats.pending})` },
            { key: "assigned", label: `Assigned (${stats.assigned})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`apdf-filter-btn ${
                state.filter === key ? "active" : ""
              }`}
              onClick={() => dispatch({ type: "SET_FILTER", payload: key })}
              aria-pressed={state.filter === key}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="apdf-view-toggle" role="group" aria-label="View mode">
          <button
            className={`apdf-icon-btn ${
              state.view === "table" ? "active" : ""
            }`}
            onClick={() => dispatch({ type: "SET_VIEW", payload: "table" })}
            aria-label="Table view"
            title="Table view"
          >
            <Icon.table />
          </button>
          <button
            className={`apdf-icon-btn ${state.view === "grid" ? "active" : ""}`}
            onClick={() => dispatch({ type: "SET_VIEW", payload: "grid" })}
            aria-label="Grid view"
            title="Grid view"
          >
            <Icon.grid />
          </button>
        </div>
      </div>

      {/* SELECTION BAR */}
      {stats.selected > 0 && (
        <div className="apdf-sel-bar" aria-live="polite">
          <span>
            {stats.selected} PDF{stats.selected !== 1 ? "s" : ""} selected
          </span>
          <span className="apdf-sel-bar-spacer" />
          <button
            className="apdf-btn apdf-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11 }}
            onClick={() =>
              dispatch({
                type: "SET_SELECTION",
                payload: pendingPdfs.map((p) => p.file_name),
              })
            }
          >
            Select All Pending
          </button>
          <button
            className="apdf-btn apdf-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11 }}
            onClick={() => dispatch({ type: "SET_SELECTION", payload: [] })}
          >
            Clear
          </button>
        </div>
      )}

      {/* TABLE or GRID */}
      {state.view === "table" ? (
        <div
          className="apdf-table-wrapper"
          style={
            stats.selected > 0
              ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
              : {}
          }
        >
          <table
            className="apdf-table"
            role="grid"
            aria-label="PDF assignment table"
            aria-rowcount={filteredData.length}
          >
            <thead>
              <tr>
                <th style={{ width: 40 }} aria-label="Select">
                  <div className="apdf-cb-wrap">
                    <input
                      type="checkbox"
                      className="apdf-cb"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el)
                          el.indeterminate = somePageSelected && !allPageSelected;
                      }}
                      onChange={handleSelectAll}
                      aria-label="Select all on this page"
                    />
                  </div>
                </th>
                <th style={{ width: 44 }}>#</th>
                <SortTh label="File Name" sortKey="file_name" />
                <SortTh label="Worker" sortKey="assigned_worker_name" />
                <SortTh label="Phone" sortKey="assigned_worker_phone_no" />
                <SortTh label="Status" sortKey="status" />
              </tr>
            </thead>
            <tbody>
              {state.loading ? (
                <SkeletonRows />
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="apdf-empty">
                      <div className="apdf-empty-icon">📭</div>
                      <div>No PDFs match your filters</div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((pdf, i) => {
                  const isPending = pdf.status !== "assigned";
                  const isSelected = state.selectedPdfs.includes(pdf.file_name);
                  const rowIndex = (state.page - 1) * PAGE_SIZE + i + 1;
                  return (
                    <tr
                      key={pdf.file_name || i}
                      className={isSelected ? "selected-row" : ""}
                      aria-selected={isSelected}
                      role="row"
                    >
                      <td>
                        <div className="apdf-cb-wrap">
                          {isPending && (
                            <input
                              type="checkbox"
                              className="apdf-cb"
                              checked={isSelected}
                              onChange={() =>
                                dispatch({
                                  type: "TOGGLE_PDF",
                                  payload: pdf.file_name,
                                })
                              }
                              aria-label={`Select ${pdf.file_name}`}
                            />
                          )}
                        </div>
                      </td>
                      <td>{rowIndex}</td>
                      <td className="apdf-filename" title={pdf.file_name}>
                        {pdf.file_name}
                      </td>
                      <td>{pdf.assigned_worker_name || "—"}</td>
                      <td>{pdf.assigned_worker_phone_no || "—"}</td>
                      <td>
                        <span
                          className={`apdf-badge ${
                            pdf.status === "assigned" ? "assigned" : "pending"
                          }`}
                        >
                          <span className="apdf-badge-dot" />
                          {pdf.status === "assigned" ? "Assigned" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {!state.loading && filteredData.length > PAGE_SIZE && (
            <div
              className="apdf-pagination"
              role="navigation"
              aria-label="Pagination"
            >
              <span>
                {(state.page - 1) * PAGE_SIZE + 1}–
                {Math.min(state.page * PAGE_SIZE, filteredData.length)} of{" "}
                {filteredData.length}
              </span>
              <div className="apdf-page-btns">
                <button
                  className="apdf-page-btn"
                  disabled={state.page === 1}
                  onClick={() =>
                    dispatch({ type: "SET_PAGE", payload: state.page - 1 })
                  }
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {pageNumbers().map((p, i) =>
                  p === "…" ? (
                    <span
                      key={`e-${i}`}
                      className="apdf-page-btn"
                      style={{ cursor: "default" }}
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`apdf-page-btn ${
                        state.page === p ? "active" : ""
                      }`}
                      onClick={() =>
                        dispatch({ type: "SET_PAGE", payload: p })
                      }
                      aria-label={`Page ${p}`}
                      aria-current={state.page === p ? "page" : undefined}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  className="apdf-page-btn"
                  disabled={state.page === totalPages}
                  onClick={() =>
                    dispatch({ type: "SET_PAGE", payload: state.page + 1 })
                  }
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* GRID VIEW */
        <div className="apdf-table-wrapper">
          {state.loading ? (
            <div className="apdf-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="apdf-card">
                  <div
                    className="apdf-skeleton-cell"
                    style={{ width: 32, height: 32, marginBottom: 8 }}
                  />
                  <div
                    className="apdf-skeleton-cell"
                    style={{ width: "80%" }}
                  />
                </div>
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="apdf-empty">
              <div className="apdf-empty-icon">📭</div>
              <div>No PDFs match your filters</div>
            </div>
          ) : (
            <div className="apdf-grid">
              {paginated.map((pdf, i) => {
                const isPending = pdf.status !== "assigned";
                const isSelected = state.selectedPdfs.includes(pdf.file_name);
                return (
                  <div
                    key={pdf.file_name || i}
                    className={`apdf-card ${isSelected ? "selected" : ""}`}
                    onClick={() =>
                      isPending &&
                      dispatch({
                        type: "TOGGLE_PDF",
                        payload: pdf.file_name,
                      })
                    }
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={isPending ? 0 : -1}
                    onKeyDown={(e) => {
                      if (isPending && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        dispatch({
                          type: "TOGGLE_PDF",
                          payload: pdf.file_name,
                        });
                      }
                    }}
                  >
                    {isPending && (
                      <div className="apdf-card-cb">
                        <input
                          type="checkbox"
                          className="apdf-cb"
                          checked={isSelected}
                          onChange={() =>
                            dispatch({
                              type: "TOGGLE_PDF",
                              payload: pdf.file_name,
                            })
                          }
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${pdf.file_name}`}
                        />
                      </div>
                    )}
                    <div className="apdf-card-icon">{Icon.pdf()}</div>
                    <div className="apdf-card-name" title={pdf.file_name}>
                      {pdf.file_name}
                    </div>
                    <span
                      className={`apdf-badge ${
                        pdf.status === "assigned" ? "assigned" : "pending"
                      }`}
                    >
                      <span className="apdf-badge-dot" />
                      {pdf.status === "assigned" ? "Assigned" : "Pending"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ASSIGN MODAL */}
      {state.showModal && (
        <div
          className="apdf-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) =>
            e.target === e.currentTarget && dispatch({ type: "HIDE_MODAL" })
          }
        >
          <div className="apdf-modal" ref={modalRef}>
            <div className="apdf-modal-header">
              <h2 className="apdf-modal-title" id="modal-title">
                Assign Worker
              </h2>
              <button
                className="apdf-icon-btn"
                onClick={() => dispatch({ type: "HIDE_MODAL" })}
                aria-label="Close modal"
              >
                <Icon.close />
              </button>
            </div>
            <div className="apdf-modal-body">
              <div
                className="apdf-selected-pills"
                aria-label="Selected PDFs"
              >
                {state.selectedPdfs.map((name) => (
                  <span key={name} className="apdf-pill" title={name}>
                    {name}
                  </span>
                ))}
              </div>

              <div className="apdf-form-group">
                <label className="apdf-label" htmlFor="worker-name">
                  Worker Name
                </label>
                <input
                  id="worker-name"
                  className={`apdf-input ${
                    state.errors.workerName ? "error" : ""
                  }`}
                  type="text"
                  placeholder="Enter full name (min 3 chars)"
                  value={state.workerName}
                  onChange={(e) =>
                    dispatch({ type: "SET_WORKER", payload: e.target.value })
                  }
                  autoComplete="name"
                  aria-describedby={
                    state.errors.workerName ? "worker-name-err" : undefined
                  }
                  aria-invalid={!!state.errors.workerName}
                />
                {state.errors.workerName && (
                  <div
                    className="apdf-field-error"
                    id="worker-name-err"
                    role="alert"
                  >
                    {state.errors.workerName}
                  </div>
                )}
              </div>

              <div className="apdf-form-group">
                <label className="apdf-label" htmlFor="phone-number">
                  Phone Number
                </label>
                <input
                  id="phone-number"
                  className={`apdf-input ${
                    state.errors.phoneNumber ? "error" : ""
                  }`}
                  type="tel"
                  placeholder="+91 98765 43210 or international"
                  value={state.phoneNumber}
                  onChange={(e) =>
                    dispatch({ type: "SET_PHONE", payload: e.target.value })
                  }
                  autoComplete="tel"
                  aria-describedby={
                    state.errors.phoneNumber ? "phone-err" : undefined
                  }
                  aria-invalid={!!state.errors.phoneNumber}
                />
                {state.errors.phoneNumber && (
                  <div
                    className="apdf-field-error"
                    id="phone-err"
                    role="alert"
                  >
                    {state.errors.phoneNumber}
                  </div>
                )}
              </div>
            </div>
            <div className="apdf-modal-footer">
              <button
                className="apdf-btn apdf-btn-ghost"
                onClick={() => dispatch({ type: "HIDE_MODAL" })}
              >
                Cancel
              </button>
              <button
                className="apdf-btn apdf-btn-primary"
                onClick={handlePreAssign}
              >
                Review & Assign →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DIALOG */}
      {state.showConfirm && (
        <div
          className="apdf-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div className="apdf-modal" style={{ maxWidth: 380 }}>
            <div className="apdf-modal-header">
              <h2 className="apdf-modal-title" id="confirm-title">
                Confirm Assignment
              </h2>
              <button
                className="apdf-icon-btn"
                onClick={() => dispatch({ type: "HIDE_CONFIRM" })}
                aria-label="Cancel"
              >
                <Icon.close />
              </button>
            </div>
            <div className="apdf-confirm-body">
              You are about to assign <strong>{state.selectedPdfs.length}</strong>{" "}
              PDF{state.selectedPdfs.length !== 1 ? "s" : ""} to:
              <div className="apdf-confirm-detail">
                <div>
                  Name: <span>{state.workerName}</span>
                </div>
                <div>
                  Phone: <span>{state.phoneNumber}</span>
                </div>
              </div>
            </div>
            <div className="apdf-modal-footer">
              <button
                className="apdf-btn apdf-btn-ghost"
                onClick={() => dispatch({ type: "HIDE_CONFIRM" })}
              >
                ← Back
              </button>
              <button
                className="apdf-btn apdf-btn-primary"
                onClick={handleAssign}
              >
                Confirm Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL PROGRESS */}
      {state.assigning && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9998,
          }}
          role="progressbar"
          aria-label="Assigning PDFs"
        >
          <div className="apdf-progress-bar" style={{ margin: 0, borderRadius: 0 }}>
            <div className="apdf-progress-fill" style={{ width: "100%" }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedPDF;